import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserPayload } from "../models/SiteModel";
import { fetchValidarUsuario } from "../services/userService";
import { useUserContext } from "../hooks/UserContext";
import { stringToArray } from "../utils/Helper";
import { TextField, Button, Box, Paper, Typography, Select, MenuItem, SelectChangeEvent, CircularProgress, IconButton, InputAdornment } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { fetchGetConections } from "../services/bdService";
import { useNotification } from "../hooks/NotificationContext";

const Login = () => {
    const { setUser } = useUserContext();
    const { showNotification } = useNotification();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [mostrarPassword, setMostrarPassword] = useState(false);
    const [connections, setConnections] = useState<string[]>([]);
    const [conexion, setConexion] = useState("");
    const [iniciandoSesion, setIniciandoSesion] = useState(false);
    const iniciandoSesionRef = useRef(false);

    const usernameRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const conexionRef = useRef<HTMLDivElement>(null);
    const showConnectionSelector = connections.length !== 1;

    const blurActiveElement = () => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    };

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const handleLogin = () => {
        if (iniciandoSesionRef.current) return;

        if (!username || !password || !conexion) {
            showNotification("Por favor, complete todos los campos.", "error");
            return;
        }
        iniciandoSesionRef.current = true;
        setIniciandoSesion(true);
        fetchValidarUsuario(username, password, conexion)
            .then(r => {
                if (!r?.token) {
                    showNotification("Usuario o contrasena incorrectos.", "error");
                    return;
                }

                const user: UserPayload = {
                    conexion,
                    username,
                    permissions: [],
                    token: r.token
                };
                const payload = JSON.parse(atob(r.token.split('.')[1]));
                user.permissions = stringToArray(payload.web_permissions, ',') || [];
                user.idEmpleado = Number(payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid"] ?? payload.sid ?? payload.Sid ?? payload.idEmpleado ?? payload.IdEmpleado ?? 0) || undefined;
                user.idCliente = Number(payload.id_cliente ?? payload.idCliente ?? payload.IDCLIENTE ?? 0) || undefined;
                user.cliente = payload.cliente ?? payload.Cliente ?? undefined;
                setUser(user);
                const returnUrl = searchParams.get("returnUrl");
                if (returnUrl && /^\/connect\/authorize(?:\?|$)/.test(returnUrl)) {
                    window.location.assign(`${process.env.REACT_APP_BASE_URL}${returnUrl}`);
                    return;
                }

                navigate(user.idCliente ? "/clientes/portal" : "/mainpage");
            })
            .catch((err) => showNotification(err?.message ?? "No se pudo iniciar sesion.", "error"))
            .finally(() => {
                iniciandoSesionRef.current = false;
                setIniciandoSesion(false);
            });
    };

    useEffect(() => {
        fetchGetConections()
            .then(connections => {
                setConnections(connections);
                if (connections.length === 1) {
                    blurActiveElement();
                    setConexion(connections[0]);
                }
                console.log("Conexiones disponibles:", connections);
            });
    }, []);

    const handleChangeConexion = (event: SelectChangeEvent) => {
        setConexion(event.target.value);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            if (showConnectionSelector && document.activeElement === conexionRef.current) {
                usernameRef.current?.focus();
            } else if (document.activeElement === usernameRef.current) {
                passwordRef.current?.focus();
            } else if (document.activeElement === passwordRef.current) {
                handleLogin();
            }
        }
    };

    return (
        <div>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ bgcolor: "#f3f6f9", px: 2 }}>
                <Paper elevation={3} sx={{ p: 4, width: "100%", maxWidth: 380, borderRadius: 2 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" sx={{ mb: 3 }}>
                        <Box
                            component="img"
                            src="/logo192.png"
                            alt="Grovia"
                            sx={{ width: 72, height: 72, mb: 1 }}
                        />
                        <Typography variant="h5" fontWeight={700}>Grovia</Typography>
                        <Typography variant="body2" color="text.secondary">Gestion Comercial</Typography>
                    </Box>
                    <Typography variant="h5" gutterBottom>Iniciar Sesión</Typography>
                    <Box display="flex" flexDirection="column" gap={2} onKeyDown={handleKeyDown}>
                        {showConnectionSelector && (
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={conexion}
                                label="Conexion"
                                onChange={handleChangeConexion}
                                ref={conexionRef}
                            >
                                {connections.map(l =>
                                (
                                    <MenuItem key={l} value={l}>{l}</MenuItem>
                                ))
                                }
                            </Select>
                        )}
                        <TextField
                            label="Usuario"
                            variant="outlined"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            fullWidth
                            inputRef={usernameRef}
                        />
                        <TextField
                            label="Contraseña"
                            variant="outlined"
                            type={mostrarPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fullWidth
                            inputRef={passwordRef}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={mostrarPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                                            onClick={() => setMostrarPassword((mostrar) => !mostrar)}
                                            edge="end"
                                        >
                                            {mostrarPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleLogin}
                            disabled={iniciandoSesion}
                            startIcon={iniciandoSesion ? <CircularProgress color="inherit" size={18} /> : undefined}
                            fullWidth
                        >
                            {iniciandoSesion ? "Ingresando..." : "Login"}
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </div>
    );
};

export default Login;
